export type CanonicalUsePattern = "acute" | "maintenance" | "mixed" | "unknown";

export function normalizeClinicalText(value: any): string {
  return String(value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/→/g, " ")
    .replace(/[^a-z0-9\s/.-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function canonicalSubstanceKey(raw: any): string {
  const n = normalizeClinicalText(raw);
  if (!n) return "";
  if (/lyberdia|venvanse|juneve|vyvanse|elvanse|lisdex|lisdexam/.test(n))
    return "lisdexanfetamina";
  if (/ritalina|concerta|foq|metilfen/.test(n)) return "metilfenidato";
  if (/rivotril|clonaz/.test(n)) return "clonazepam";
  if (/atentah|strattera|atomox/.test(n)) return "atomoxetina";
  if (/vurtuoso|brintellix|trintellix|voextor|vortiox/.test(n))
    return "vortioxetina";
  if (/depak|depakene|torval|valpakine|dival|valpro/.test(n))
    return "valproato";
  if (/lamictal|lamotrig/.test(n)) return "lamotrigina";
  if (/quetiap/.test(n)) return "quetiapina";
  if (/clonidin|atensina|catapres/.test(n)) return "clonidina";
  if (/buprop|wellbutrin|zetron/.test(n)) return "bupropiona";
  if (/desvenlaf|pristiq/.test(n)) return "desvenlafaxina";
  if (/venlaf/.test(n)) return "venlafaxina";
  if (/sertralin/.test(n)) return "sertralina";
  if (/escitalop/.test(n)) return "escitalopram";
  if (/fluvox/.test(n)) return "fluvoxamina";
  if (/fluox/.test(n)) return "fluoxetina";
  if (/parox/.test(n)) return "paroxetina";
  if (/dulox/.test(n)) return "duloxetina";
  if (/pregabal/.test(n)) return "pregabalina";
  if (/zolpid/.test(n)) return "zolpidem";
  if (/cafein|cafeina/.test(n)) return "cafeina";
  if (/alcool|etanol/.test(n)) return "alcool";
  if (/oxicodon/.test(n)) return "oxicodona";
  if (/codein|codeina/.test(n)) return "codeina";
  if (/metadon/.test(n)) return "metadona";
  if (/buprenorf/.test(n)) return "buprenorfina";
  if (/tramadol/.test(n)) return "tramadol";
  return n.split(" ").slice(-2).join(" ").trim();
}

export function canonicalDisplayLabel(raw: any): string {
  const key = canonicalSubstanceKey(raw);
  const labels: Record<string, string> = {
    lisdexanfetamina: "Lisdexanfetamina",
    metilfenidato: "Metilfenidato",
    clonazepam: "Clonazepam",
    atomoxetina: "Atomoxetina",
    vortioxetina: "Vortioxetina",
    valproato: "Valproato/Divalproato",
    lamotrigina: "Lamotrigina",
    quetiapina: "Quetiapina",
    clonidina: "Clonidina",
    bupropiona: "Bupropiona",
    cafeina: "Cafeína",
    alcool: "Álcool",
  };
  return (
    labels[key] ??
    String(raw ?? "substância")
      .split("→")
      .pop()
      ?.trim() ??
    String(raw ?? "substância")
  );
}

export function isExplicitRedoseLike(logType?: any): boolean {
  return /redose|extra|problem|abuso|misuse|uso_problematico|uso problem/.test(
    normalizeClinicalText(logType),
  );
}

export function isMaintenanceLikeLog(logType?: any): boolean {
  const n = normalizeClinicalText(logType);
  return (
    !n ||
    /manutencao|manutencao|manuten|continuo|continua|regular|diario|diaria/.test(
      n,
    )
  );
}

export function isPrnLikeLog(logType?: any): boolean {
  return /prn|sos|resgate/.test(normalizeClinicalText(logType));
}

export function isStimulantKey(key: string): boolean {
  return /lisdexanfetamina|metilfenidato|anfetamina|cocaina/.test(key);
}

export function isLongHalfLifeMaintenanceKey(key: string): boolean {
  return /atomoxetina|vortioxetina|valproato|lamotrigina|fluoxetina|sertralina|escitalopram|fluvoxamina|paroxetina|desvenlafaxina|venlafaxina|duloxetina/.test(
    key,
  );
}

export function isPotentiallyAcuteOrPrnKey(key: string): boolean {
  return (
    isStimulantKey(key) ||
    /clonazepam|alprazolam|diazepam|lorazepam|zolpidem|quetiapina|pregabalina|opio|oxicodona|codeina|tramadol|metadona|buprenorfina|cafeina|alcool/.test(
      key,
    )
  );
}

export function inferUsePatternFromNameAndLog(
  name: any,
  logType?: any,
): CanonicalUsePattern {
  const key = canonicalSubstanceKey(name);
  if (isExplicitRedoseLike(logType) || isPrnLikeLog(logType)) return "acute";
  if (isLongHalfLifeMaintenanceKey(key) && isMaintenanceLikeLog(logType))
    return "maintenance";
  if (isStimulantKey(key)) return "acute";
  if (isPotentiallyAcuteOrPrnKey(key)) return "mixed";
  return "unknown";
}

export function shouldFlagSelfOverlap(input: {
  name: any;
  logType?: any;
  gapHours?: number | null;
  explicitRedoseInGroup?: boolean;
}): boolean {
  const key = canonicalSubstanceKey(input.name);
  if (!key) return false;
  if (isExplicitRedoseLike(input.logType) || input.explicitRedoseInGroup)
    return true;
  if (isLongHalfLifeMaintenanceKey(key) && isMaintenanceLikeLog(input.logType))
    return false;
  if (!isPotentiallyAcuteOrPrnKey(key)) return false;
  return true;
}

export function uniqueCanonicalLabels(names: any[]): string[] {
  const map = new Map<string, string>();
  for (const name of names) {
    const key = canonicalSubstanceKey(name);
    if (!key) continue;
    if (!map.has(key)) map.set(key, canonicalDisplayLabel(name));
  }
  return Array.from(map.values());
}

export function uniqueCanonicalKeys(names: any[]): string[] {
  return Array.from(new Set(names.map(canonicalSubstanceKey).filter(Boolean)));
}

export function looksDailyFrequency(freq: any): boolean {
  const n = normalizeClinicalText(freq);
  return /1x|1 x|dia|diari|manha|noite|deitar|jantar|almoco|continuo|regular/.test(
    n,
  );
}
